/**
 * ForecastHero.tsx — Forecast programme F2 (register row 70): the smart
 * forecast hero, spec §3.2, rendered from forecastHero.ts's model and
 * nothing else. Three columns — the headline with its sentence, scenario
 * switch and bar; "What matters now"; the two rings — over a footer strip.
 *
 * Solid panels only (the spec's own warning: no translucent or glass
 * grounds, no radial gradients). Every colour is a theme token — the rings
 * read `forecastRing` (won = accent, elapsed = the spec's indigo through
 * --k-indigo, never its hex).
 * The as-of time replaces the spec's Live dot (§18.7): the page reads once,
 * so it must not claim to recalculate.
 *
 * F6 (spec §3.8): the scenario is the user's pick, else the period's default
 * (a future period opens on Stretch); an ENDED period's panel is the
 * retrospective, and this component makes its one extra read — the period's
 * closed-won deals, for the largest-deal card — only then, keyed on the
 * response's own period and won figure so a silent refetch that moves
 * neither does not read again. While that read is out the card's place is a
 * blank block, never a figure.
 */
import { useEffect, useState } from 'react'
import { Lock, TrendingUp, Target, TrendingDown, Star, AlertTriangle, Sparkles, Award, Banknote, type LucideIcon } from 'lucide-react'
import type { SalesForecast } from '../../../../api/types'
import { accent, info, success, warning, danger, shadow, forecastRing } from '../../../../theme/tokens'
import { fetchDeals } from '../../api'
import type { SalesDeal } from '../../types'
import { heroModel, defaultScenario, wonDealsQuery, type Insight, type Scenario, type WonDealsRead } from './forecastHero'

const RULE_ICON: Record<Insight['rule'], LucideIcon> = {
  2: TrendingUp, 3: Target, 4: TrendingDown, 5: Star, 6: AlertTriangle,
  target: Target, bestRep: Award, largestDeal: Banknote,
}
const TONE = {
  info: { bg: info.bg, fg: info.text },
  warning: { bg: warning.bg, fg: warning.text },
  danger: { bg: danger.bg, fg: danger.text },
  success: { bg: success.bg, fg: success.text },
} as const

const RING = { box: 184, stroke: 16, gap: 4 }

export function ForecastHero({ data, onSelectDeal }: { data: SalesForecast; onSelectDeal?: (id: string) => void }) {
  // F6: null until the user presses a segment — until then the period's own
  // default applies (Stretch for a future period, Locked otherwise).
  const [picked, setPicked] = useState<Scenario | null>(null)
  // F6: the retrospective's second read. `q` is null off an ended period (and
  // on one with nothing won), so no other state ever makes the request.
  const q = wonDealsQuery(data)
  const qKey = q?.key ?? null, closedFrom = q?.closedFrom, closedTo = q?.closedTo
  const [read, setRead] = useState<{ key: string; deals: SalesDeal[] | null } | null>(null)
  useEffect(() => {
    if (!qKey) return
    let alive = true
    fetchDeals({ stage: 'closed_won', closedFrom, closedTo })
      .then(deals => { if (alive) setRead({ key: qKey, deals }) })
      .catch(() => { if (alive) setRead({ key: qKey, deals: null }) })
    return () => { alive = false }
  }, [qKey, closedFrom, closedTo])
  const wonDeals: WonDealsRead = !qKey ? { status: 'idle' }
    : !read || read.key !== qKey ? { status: 'loading' }
      : read.deals === null ? { status: 'error' } : { status: 'ready', deals: read.deals }
  const m = heroModel(data, picked ?? defaultScenario(data), wonDeals)
  if (!m) return null
  return (
    <section className="k-card" aria-label="Smart forecast" data-hero-scenario={m.scenario} data-period-state={m.clock.state}>
      <div className="grid gap-6 items-stretch" style={{ padding: 28, gridTemplateColumns: '1.25fr 1fr 300px' }}>
        {/* Column 1 — the headline */}
        <div className="flex flex-col gap-3 min-w-0">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1 rounded-full px-[10px] py-[3px] text-12 font-semibold"
              style={{ background: info.bg, color: accent.DEFAULT }}>
              <Sparkles size={12} aria-hidden="true" />Smart forecast
            </span>
            <span className="text-12 text-ink-muted" data-as-of={data.asOf}>{m.asOfLabel}</span>
          </div>
          <div className="text-ink-secondary" style={{ fontSize: 17 }}>{m.label}</div>
          <div className="text-ink-primary" title={m.headlineExact} data-headline-pence={m.headline.pence}
            style={{ fontSize: 76, fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1 }}>
            {m.headlineText}
          </div>
          <p className="text-ink-body" style={{ fontSize: 15 }} data-hero-sentence>{m.sentence}</p>
          {m.fenceNote && <p className="text-12 text-ink-muted">{m.fenceNote}</p>}
          {m.showSwitch && (
            <div role="group" aria-label="Scenario" className="inline-flex self-start gap-[2px] p-[3px] rounded-full bg-surface-control">
              {(['locked', 'stretch'] as const).map(s => {
                const Icon = s === 'locked' ? Lock : TrendingUp
                const on = m.scenario === s
                return (
                  <button key={s} type="button" aria-pressed={on} onClick={() => setPicked(s)}
                    className={`inline-flex items-center gap-1 px-3 rounded-full text-13 ${on ? 'bg-surface-card font-semibold text-ink-primary' : 'text-ink-secondary'}`}
                    style={{ height: 30, boxShadow: on ? shadow.card : undefined }}>
                    <Icon size={13} aria-hidden="true" />{s === 'locked' ? 'Locked' : 'Stretch'}
                  </button>
                )
              })}
            </div>
          )}
          <div className="flex flex-col gap-2 mt-1">
            <div className="flex overflow-hidden bg-surface-control" style={{ height: 10, borderRadius: 5 }}
              role="img" aria-label={`${m.bar.left}${m.bar.right ? `; ${m.bar.right}` : ''}`}>
              <div style={{ width: `${m.bar.wonPct}%`, background: accent.DEFAULT }} data-bar="won" />
              {m.bar.stretchPct > 0 && (
                <div data-bar="stretch" style={{ width: `${m.bar.stretchPct}%`,
                  background: `repeating-linear-gradient(135deg, ${accent.soft} 0 4px, ${info.bg} 4px 8px)` }} />
              )}
            </div>
            <div className="flex justify-between text-12 text-ink-muted">
              <span>{m.bar.left}</span>{m.bar.right && <span>{m.bar.right}</span>}
            </div>
          </div>
        </div>

        {/* Column 2 — "What matters now" */}
        <div className="flex flex-col gap-3 p-4 bg-surface-panel min-w-0" style={{ borderRadius: 16 }} aria-label={m.insightsTitle} role="region"
          data-largest-deal={m.largest.state}>
          <div className="text-13 font-semibold text-ink-primary">{m.insightsTitle}</div>
          {m.insights.length === 0 && <p className="text-13 text-ink-muted">Nothing needs attention in {m.period}.</p>}
          {m.insights.map(i => {
            const Icon = RULE_ICON[i.rule]
            return (
              <div key={i.rule} className="flex gap-3 p-3 bg-surface-card" style={{ borderRadius: 12 }} data-insight-rule={i.rule}>
                <span className="flex-shrink-0 inline-flex items-center justify-center"
                  style={{ width: 32, height: 32, borderRadius: 10, background: TONE[i.tone].bg, color: TONE[i.tone].fg }}>
                  <Icon size={16} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="text-ink-primary" style={{ fontSize: 14, fontWeight: 600 }}>{i.title}</div>
                  <p className="text-13 text-ink-muted">{i.body}</p>
                  {i.link && onSelectDeal && (
                    <button type="button" className="text-13 text-accent font-medium hover:underline" onClick={() => onSelectDeal(i.link!.dealId)}>
                      {i.link.label}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
          {/* F6: the largest-deal card's place while its read is out — a
              blank block, no figure and no sentence to be wrong. */}
          {m.largest.state === 'loading' && (
            <div className="k-skeleton" style={{ height: 64, borderRadius: 12 }} aria-hidden="true" data-insight-rule="largestDeal" data-insight-state="loading" />
          )}
        </div>

        {/* Column 3 — the rings */}
        <div className="flex flex-col items-center gap-3 p-4 bg-surface-panel" style={{ borderRadius: 16 }}>
          <Rings outer={m.rings.outerPct} inner={m.rings.elapsedPct} label={m.rings.ariaLabel} />
          <div className="flex flex-col gap-1 text-12 self-stretch">
            <LegendRow color={forecastRing.won} label={m.rings.outerLabel} value={m.rings.outerPct === null ? 'not measurable' : `${m.rings.outerPct}%`} />
            <LegendRow color={forecastRing.elapsed} label={m.rings.elapsedLabel} value={`${m.rings.elapsedPct}%`} />
            <div className="text-ink-muted mt-1" data-ring-caption>{m.rings.caption}</div>
          </div>
        </div>
      </div>
      {m.footer && (
        <div className="mx-7 mb-7 px-4 py-3 text-13 text-ink-body bg-surface-panel" style={{ borderRadius: 12 }} data-footer={m.footer.kind}>
          {m.footer.text}
        </div>
      )}
    </section>
  )
}

function LegendRow({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block rounded-full flex-shrink-0" style={{ width: 8, height: 8, background: color }} />
      <span className="text-ink-secondary flex-1">{label}</span>
      <span className="text-ink-primary font-semibold">{value}</span>
    </div>
  )
}

function Rings({ outer, inner, label }: { outer: number | null; inner: number; label: string }) {
  const c = RING.box / 2
  const rOuter = c - RING.stroke / 2
  const rInner = rOuter - RING.stroke - RING.gap
  const arc = (r: number, pct: number) => {
    const len = 2 * Math.PI * r
    const p = Math.max(0, Math.min(100, pct))
    return { strokeDasharray: `${(len * p) / 100} ${len}` }
  }
  return (
    <div className="relative" style={{ width: RING.box, height: RING.box }} data-chart="forecast-rings">
      <svg width={RING.box} height={RING.box} viewBox={`0 0 ${RING.box} ${RING.box}`} role="img" aria-label={label}>
        <circle cx={c} cy={c} r={rOuter} fill="none" stroke={forecastRing.won} strokeOpacity={0.13} strokeWidth={RING.stroke} />
        {outer !== null && outer > 0 && (
          <circle cx={c} cy={c} r={rOuter} fill="none" stroke={forecastRing.won} strokeWidth={RING.stroke} strokeLinecap="round"
            transform={`rotate(-90 ${c} ${c})`} {...arc(rOuter, outer)} data-ring="outer" />
        )}
        <circle cx={c} cy={c} r={rInner} fill="none" stroke={forecastRing.elapsed} strokeOpacity={0.13} strokeWidth={RING.stroke} />
        {inner > 0 && (
          <circle cx={c} cy={c} r={rInner} fill="none" stroke={forecastRing.elapsed} strokeWidth={RING.stroke} strokeLinecap="round"
            transform={`rotate(-90 ${c} ${c})`} {...arc(rInner, inner)} data-ring="inner" />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-ink-primary" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1 }}>{outer === null ? '—' : `${outer}%`}</span>
        <span className="text-ink-secondary" style={{ fontSize: 11 }}>won</span>
      </div>
    </div>
  )
}
